import type {
  GeneratedIntakeFieldResponse,
  IntakeHubDocumentExtractedFieldResponse,
  IntakeHubDocumentFieldSuggestionResponse,
  IntakeHubDocumentInventoryItemResponse,
  IntakeHubDocumentItemLinkResponse,
} from '@store/generated/intake-hub';

type SuggestionDisplayField = Pick<
  GeneratedIntakeFieldResponse,
  'data_type' | 'destination' | 'field_id' | 'is_writable' | 'label'
>;

const SENSITIVE_SUGGESTION_DATA_TYPES = new Set(['file', 'secret', 'ssn']);

export function canDisplayDocumentSuggestion(field: SuggestionDisplayField): boolean {
  return (
    field.is_writable !== false &&
    field.destination.kind === 'intake_item_evidence' &&
    !SENSITIVE_SUGGESTION_DATA_TYPES.has(field.data_type.toLowerCase())
  );
}

export interface IntakeDocumentSuggestedField {
  label: string;
  suggestion: IntakeHubDocumentFieldSuggestionResponse;
}

export interface IntakeDocumentDetail {
  id: string;
  sourceArtifactId?: string | null;
  title: string;
  docKind?: string | null;
  documentDomain?: string | null;
  createdAt?: string | null;
  extractedFields?: IntakeHubDocumentExtractedFieldResponse[];
  suggestedFields?: IntakeDocumentSuggestedField[];
  linkedItems?: IntakeHubDocumentItemLinkResponse[];
}

function humanize(value?: string | null): string {
  if (!value) return 'Document';
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());
}

export function intakeDocumentInventoryItemToDetail(
  documentItem: IntakeHubDocumentInventoryItemResponse,
  fields: readonly SuggestionDisplayField[] = [],
): IntakeDocumentDetail {
  const document = documentItem.document_reference;
  const visibleFields = new Map(
    fields.filter(canDisplayDocumentSuggestion).map(field => [field.field_id, field]),
  );
  return {
    id: document.document_reference_id,
    sourceArtifactId: document.source_artifact_id,
    title: document.title || humanize(document.doc_kind),
    docKind: document.doc_kind,
    documentDomain: document.document_domain,
    createdAt: document.created_at,
    extractedFields: documentItem.extracted_fields,
    // Fail closed when field metadata is unavailable. A suggestion value must
    // never become visible merely because an inventory endpoint returned it.
    suggestedFields: (documentItem.field_suggestions ?? [])
      .filter(suggestion => visibleFields.has(suggestion.field_id))
      .map(suggestion => ({
        label: visibleFields.get(suggestion.field_id)?.label ?? 'Suggested field',
        suggestion,
      })),
    linkedItems: documentItem.linked_items,
  };
}
